import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ProfessionalCard from './ProfessionalCard';
import styles from './ProfessionalsList.module.scss';
import placeholder from "../../assets/images/Bd_Placeholder.png";
import Loading from '../../components/ui/Loading/Loading';
import Pagination from '../../components/ui/Pagination/Pagination';
import { useProfessionalsList } from './hooks/useProfessionalsList';
import { HolisticSearch } from '../../components/Landing';

const ProfessionalsList: React.FC = () => {
  const { t } = useTranslation();
  const apiUrl = process.env.REACT_APP_API_URL;
  const searchRef = useRef<HTMLElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const {
    professionals,
    loading,
    error,
    pagination,
    hasActiveFilters,
    isFromSearch,
    handlePageChange,
    clearFilters
  } = useProfessionalsList();

  if (loading) {
    return <Loading fullscreen text="Cargando profesionales..." />;
  }

  const isSearchActive = hasActiveFilters || isFromSearch;
  const hasNoResults = Boolean(error || !professionals || professionals.length === 0);
  const totalCount = pagination.total ?? (professionals ? professionals.length : 0);

  const handleClearFilters = () => {
    clearFilters();
    searchRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleShowAll = () => {
    clearFilters();
    resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const onPaginationChange = (page: number) => {
    handlePageChange(page);
    resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className={styles.professionals}>
      <div
        id="professionalSearchFlow"
        className={`${styles['professional-search-flow']} ${isSearchActive ? styles['is-search-active'] : ''}`}
      >
        <section
          ref={searchRef}
          id="professionalSearch"
          className={styles['search-holder']}
          aria-label="Buscador de profesionales"
        >
          {isSearchActive && !hasNoResults && (
            <div
              id="searchActions"
              className={styles['search-actions']}
              aria-label="Resultados y acciones de búsqueda"
            >
              <p
                id="gridCount"
                className={styles['search-actions__count']}
                aria-live="polite"
                role="status"
              >
                {t('landing.professionalsList.countFound', {
                  count: totalCount,
                  defaultValue: `${totalCount} profesional${totalCount === 1 ? '' : 'es'} encontrado${totalCount === 1 ? '' : 's'}`
                })}
              </p>
              <div className={styles['search-actions__buttons']}>
                <button
                  id="clearFilters"
                  type="button"
                  className={`${styles['search-action']} ${styles['search-action--clear']}`}
                  onClick={handleClearFilters}
                >
                  {t('landing.professionalsList.clearFilters', 'Limpiar filtros')}
                </button>
                <button
                  id="showAllProfessionals"
                  type="button"
                  className={styles['search-action']}
                  onClick={handleShowAll}
                >
                  {t('landing.professionalsList.showAll', 'Ver todos los profesionales')}
                </button>
              </div>
            </div>
          )}

          <HolisticSearch className={styles['professionals-search-override']} />
        </section>

        <div
          ref={resultsRef}
          id="professionalResults"
          className={styles['professional-results']}
          aria-label="Resultados de profesionales"
        >
          {hasNoResults ? (
            <section
              id="emptyResults"
              className={styles['empty-results']}
              aria-labelledby="emptyResultsTitle"
            >
              <div className={styles['empty-results__copy']}>
                <p className={styles['empty-results__count']} role="status" aria-live="polite">
                  {t('landing.professionalsList.emptyCount', '0 profesionales encontrados')}
                </p>
                <h2 id="emptyResultsTitle">
                  {t('landing.professionalsList.emptyTitle', 'No encontramos coincidencias')}
                </h2>
                <p className={styles['empty-results__description']}>
                  {t(
                    'landing.professionalsList.emptyDescription',
                    'No hay profesionales que coincidan con estos filtros. Podés ajustar la búsqueda o explorar otras profesiones y prácticas disponibles en Bodhiwell.'
                  )}
                </p>
              </div>
              <div
                className={styles['empty-results__actions']}
                aria-label="Opciones para continuar la búsqueda"
              >
                <button
                  id="viewAllFromEmpty"
                  type="button"
                  className={styles['empty-results__primary']}
                  onClick={handleShowAll}
                >
                  {t('landing.professionalsList.showAll', 'Ver todos los profesionales')}
                </button>
              </div>
            </section>
          ) : (
            <>
              <section
                id="professionalsGrid"
                className={styles['grid-wrap']}
                aria-label="Perfiles profesionales"
              >
                {professionals.map((prof) => (
                  <ProfessionalCard
                    key={prof.uid}
                    uid={prof.uid}
                    professional={{
                      name: `${prof.first_name} ${prof.last_name}`,
                      slug: prof.slug,
                      profession: prof.public_title || prof.base_profession?.name || '',
                      specialty: prof.main_specialty?.name || 'Especialidad por definir',
                      dimension: prof.main_specialty?.dimension?.name || 'CUERPO',
                      description: prof.short_description || '',
                      image: prof.image ? `${apiUrl}${prof.image}` : placeholder,
                      mediaType: prof.profile_media_type ?? 'image',
                      videoUrl: prof.profile_video_url || undefined,
                      isMspVerified: prof.is_msp_verified || false,
                      additionalSpecialtiesCount: prof.specialties_count
                        ? Math.max(0, prof.specialties_count - 1)
                        : 0
                    }}
                  />
                ))}
              </section>

              {pagination.last_page > 1 && (
                <Pagination
                  currentPage={pagination.current_page}
                  totalCount={pagination.total}
                  pageSize={pagination.per_page || 12}
                  onPageChange={onPaginationChange}
                />
              )}

              {!isSearchActive && (
                <footer id="gridFooter" className={styles['grid-footer']}>
                  <p id="gridCountDefault" className={styles['grid-count']}>
                    {t('landing.professionalsList.countAvailable', {
                      count: totalCount,
                      defaultValue: `${totalCount} profesional${totalCount === 1 ? '' : 'es'} disponible${totalCount === 1 ? '' : 's'}`
                    })}
                  </p>
                </footer>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfessionalsList;